import React, { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { api, errorCode, errorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { tDynamic } from "@/i18n/dynamic";
import { tk } from "@/i18n";

export const BULK_MAX = 25;
const KINDS = [["email", tk("Email only"), "email"], ["phone", tk("Phone only"), "phone"], ["contact", tk("Email & phone"), "enrich"]];
const busyLead = (l) => [l.enrichment, l.enrichment_email, l.enrichment_phone].some((e) => ["queued", "running"].includes(e?.status));

function KindPicker({ kind, setKind, tariffs }) {
  const { t } = useI18n();
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="bulk-kind-picker">
      {KINDS.map(([k, label, tk_]) => (
        <button key={k} onClick={() => setKind(k)} data-testid={`bulk-kind-${k}`}
          className={`rounded-lg border p-2.5 text-left transition-colors ${kind === k ? "border-primary bg-accent" : "hover:bg-muted"}`}>
          <div className="text-xs font-semibold">{t(label)}</div>
          <div className="text-[11px] font-mono text-muted-foreground">{t("{{count}} cr. / lead", { count: tariffs?.[tk_] ?? "…" })}</div>
        </button>
      ))}
    </div>
  );
}

function Summary({ s }) {
  const { t } = useI18n();
  return (
    <div className="space-y-3 text-sm" data-testid="bulk-enrich-summary">
      <div className="grid grid-cols-3 gap-2">
        {[[t("Leads to enrich"), s.eligible.length, "bulk-eligible-count"], [t("Total cost"), t("{{count}} credits", { count: s.total_credits }), "bulk-total-credits"], [t("Available"), t("{{count}} credits", { count: s.balance }), "bulk-balance"]].map(([l, v, t]) => (
          <div key={l} className="rounded-lg bg-muted p-3"><div className="text-[11px] text-muted-foreground">{l}</div><div className="font-mono font-bold" data-testid={t}>{v}</div></div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">{t("{{count}} credits per lead, charged only when the requested contact is found — otherwise refunded.", { count: s.cost_per_lead })}</p>
      {s.skipped.length > 0 && (
        <div data-testid="bulk-skipped-list"><div className="text-xs font-semibold mb-1">{t("Skipped ({{count}})", { count: s.skipped.length })}</div>
          <div className="max-h-32 overflow-y-auto space-y-1">{s.skipped.map((k) => <div key={k.candidate_id} className="text-xs rounded bg-muted px-2 py-1"><b>{k.name}</b> — {tDynamic(k.reason)}</div>)}</div>
        </div>
      )}
    </div>
  );
}

export function BulkEnrichBar({ leads, selected, setSelected, onQueued }) {
  const { setUpgradeOpen, refresh } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState("email");
  const [tariffs, setTariffs] = useState(null);
  const selectable = leads.filter((l) => !busyLead(l));
  const all = selectable.length > 0 && selectable.slice(0, BULK_MAX).every((l) => selected.has(l.candidate_id));
  const toggleAll = () => setSelected(all ? new Set() : new Set(selectable.slice(0, BULK_MAX).map((l) => l.candidate_id)));
  const call = (dry, k = kind) => api.post("/ai/enrich/bulk", { candidate_ids: [...selected], kind: k, dry_run: dry });
  const fail = (e) => { if (["upgrade_required", "insufficient_credits"].includes(errorCode(e))) setUpgradeOpen(true); else toast.error(errorDetail(e)); };
  const preview = async (k = kind) => {
    setBusy(true);
    try { if (!tariffs) api.get("/ai/status").then((r) => setTariffs(r.data.tariffs)); setSummary((await call(true, k)).data); } catch (e) { fail(e); } finally { setBusy(false); }
  };
  const pick = (k) => { setKind(k); preview(k); };
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await call(false);
      toast.success(t("{{count}} enrichments started · {{credits}} credits reserved", { count: data.queued.length, credits: data.total_credits }));
      setSummary(null); setSelected(new Set()); refresh?.(); onQueued();
    } catch (e) { fail(e); } finally { setBusy(false); }
  };
  if (!leads.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 mb-2 text-xs" data-testid="bulk-enrich-bar">
      <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={all} onCheckedChange={toggleAll} data-testid="bulk-select-all" />{selectable.length > BULK_MAX ? t("Select all (first {{max}})", { max: BULK_MAX }) : t("Select all")}</label>
      <span className="text-muted-foreground" data-testid="bulk-selected-count">{t("{{count}} selected · max {{max}}", { count: selected.size, max: BULK_MAX })}</span>
      <Button size="sm" className="ml-auto" disabled={!selected.size || busy} onClick={() => preview()} data-testid="bulk-enrich-button">
        {busy && !summary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}{t("Enrich selected ({{count}})", { count: selected.size })}
      </Button>
      <Dialog open={!!summary} onOpenChange={(o) => !o && setSummary(null)}>
        <DialogContent className="max-w-md" data-testid="bulk-enrich-dialog">
          <DialogHeader><DialogTitle>{t("Confirm bulk enrichment")}</DialogTitle></DialogHeader>
          <KindPicker kind={kind} setKind={pick} tariffs={tariffs} />
          {summary && <Summary s={summary} />}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSummary(null)} data-testid="bulk-enrich-cancel">{t("Cancel")}</Button>
            <Button disabled={busy || !summary?.eligible.length || summary.total_credits > summary.balance} onClick={confirm} data-testid="bulk-enrich-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{t("Enrich {{count}} leads · {{credits}} credits", { count: summary?.eligible.length || 0, credits: summary?.total_credits || 0 })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
